import { ChatBLKIcon } from './components/header-chatblk-button/ChatBlkIcon';
import {
	getCurrentBreakpoint,
	isMobile,
} from '../../../../_helpers/breakpoints';
import { generateStoreData } from './_helpers/data';
import {
	HeaderData,
	NotificationsData,
	HeaderStoreData,
} from '../../../../components/blackrock/advisor-center/_types/header';
import React, { useEffect, useRef, useState } from 'react';
import HeaderSearch from './components/header-search/HeaderSearch';
import HeaderUserTypeDesktop from './components/header-user-type-desktop/HeaderUserTypeDesktop';
import HeaderLogo from './components/header-logo/HeaderLogo';
import HeaderHamburgerButton from './components/header-hamburger-button/HeaderHamburgerButton';
import HeaderNavigation from './components/header-navigation-wrapper/HeaderNavigation';
import HeaderNotificationMenuItem from './components/header-notification-menu-item/HeaderNotificationMenuItem';
import HeaderPrimaryNavigation from './components/header-primary-navigation/HeaderPrimaryNavigation';
import HeaderSecondaryNavigation from './components/header-secondary-navigation/HeaderSecondaryNavigation';
import HeaderMyProfile from './components/header-my-profile/HeaderMyProfile';
import HeaderUserTypeMobile from './components/header-user-type-mobile/HeaderUserTypeMobile';
import Overlay from '../../../../components/blackrock/advisor-center/overlay/Overlay';
import { Menu } from '../../../../components/blackrock/advisor-center/_types/navigation';
import AlertBar from '../../../../components/blackrock/advisor-center/alert-bar/AlertBar';
import { isCwp } from '../../../../_helpers/isCwp.tsx';
import MainNavToaster from './components/header-toaster/MainNavToaster';
import { useHydrated } from '../../../../_helpers/hooks/useHydrated.ts';
import { useResizeObserver } from '../../../../_helpers/hooks/useResizeObserver.ts';
import { setHeaderHeight } from './_helpers/setHeaderHeight.ts';
import { isCwpUserSignedIn } from '../../../../_helpers/isCwpUserSignedIn.ts';
import useNotificationApi from './_helpers/hooks/useNotificationApi.ts';

import './Header.scss';

export type HeaderProps = {
	headerData: HeaderData;
};

const Header: React.FC<HeaderProps> = ({ headerData }) => {
	const [coreData, setCoreData] = useState<HeaderStoreData>(
		generateStoreData(headerData)
	);
	const [isExpanded, setIsExpanded] = useState<boolean>(false);
	const [isOpened, setIsOpened] = useState<boolean>(false);
	const [isMobileView, setIsMobileView] = useState<boolean>(false);
	const [isSearchOpened, setIsSearchOpened] = useState<boolean>(false);
	const [isProfileOpened, setIsProfileOpened] = useState<boolean>(false);
	const [isUserTypeOpened, setIsUserTypeOpened] = useState<boolean>(true);
	const [isUserTypeVisible, setIsUserTypeVisible] = useState<boolean>(
		!!headerData?.ribbon
	);
	const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);

	const headerContainer = useRef<HTMLDivElement>();
	const headerElement = useRef<HTMLDivElement>();

	const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] =
		useState(false);

	useEffect(() => {
		setIsSearchOpened(false);
		setIsProfileOpened(false);

		if (isExpanded) {
			setIsNotificationDrawerOpen(false);
		}
	}, [isExpanded]);

	useEffect(() => {
		if (isNotificationDrawerOpen && isExpanded) {
			setIsExpanded(false);
		}
	}, [isNotificationDrawerOpen]);

	const [primaryNavItemIndex, setPrimaryNavItemIndex] = useState<number>(0);
	const [secondaryNavItemIndex, setSecondaryNavItemIndex] = useState<number>(0);

	const [menu, setMenu] = useState<Menu[]>([...coreData.menu]);

	const isOverlayVisible = isOpened && isMobileView;

	// eslint-disable-next-line @typescript-eslint/ban-ts-comment
	// @ts-ignore
	useResizeObserver(headerElement.current, setHeaderHeight);

	useEffect(() => {
		if (!headerData) throw Error('Empty raw data');

		window.addEventListener('resize', breakpointCheck);
		breakpointCheck();

		return () => {
			window.removeEventListener('resize', breakpointCheck);
		};
	}, [headerData]);

	const useNotificationApiResult = useNotificationApi(
		headerData?.notificationsData?.notificationsUrl,
		headerData?.notificationsData?.markAllAsReadItem?.href,
		isLoggedIn,
		headerData?.notificationsData?.typeErrors
	);

	const { newCountLabel } = useNotificationApiResult;

	useEffect(() => {
		const preparedCoreData = generateStoreData(headerData, newCountLabel);

		setCoreData(preparedCoreData);

		setMenu([...preparedCoreData.menu]);
	}, [headerData, newCountLabel, isLoggedIn]);

	useEffect(() => {
		if (isOpened && isMobileView) {
			document.body.classList.add('is-scroll-hidden');
		} else {
			document.body.classList.remove('is-scroll-hidden');
		}
	}, [isOpened]);

	useEffect(() => setIsLoggedIn(isCwpUserSignedIn), [isLoggedIn]);

	const breakpointCheck = () => {
		const currentBreakpoint = getCurrentBreakpoint();
		const isMobileView = isMobile(currentBreakpoint);

		setIsMobileView(isMobileView);
	};

	const toggleSearch = (state: boolean): void => {
		if (isMobileView) {
			// setting isExpanded from true to false causes setIsSearchOpened(false) to run in useEffect (on top of this component),
			// causing the search bar to close unintentionally
			resetStateOnMobile({ resetIsExpanded: false });
		}

		setIsOpened(false);
		setIsSearchOpened(state);

		if (state) {
			setIsNotificationDrawerOpen(false);
		}
	};

	const resetStateOnMobile = (
		{ resetIsExpanded } = { resetIsExpanded: true }
	) => {
		setCoreData({
			...coreData,
			shouldFocusOnFirst: false,
			shouldFocusOnLast: false,
		});

		setIsUserTypeOpened(false);
		setIsProfileOpened(false);
		if (resetIsExpanded) {
			setIsExpanded(false);
		}
		setIsSearchOpened(false);
	};

	const hydrated = useHydrated();

	const lastSetExpandedCallRef = useRef<null | boolean>(null);

	/**
	 * This is a solution for the following issue:
	 * On desktop, hovering over a primary nav item will expand the secondary nav
	 * however, if the user moves the cursor to the expanded secondary nav slowly, the state updates are not predictable and cause flickering
	 * @param value
	 */
	const batchedSetExpanded = (value: boolean) => {
		lastSetExpandedCallRef.current = value;

		if (value) {
			setIsExpanded(true);
			return;
		}

		setTimeout(() => {
			// if the previous batchedSetExpanded call was with parameter of 'true', then don't close the secondary navbar
			if (lastSetExpandedCallRef.current === true) {
				return;
			}
			setIsExpanded(false);
		}, 100); // this 100ms delay on close is not noticeable by the user
	};

	const showNotificationAndHideProfileMenu = !!headerData.notificationsData;

	return (
		coreData && (
			<>
				<div
					className={`blk-ac-header ${
						isMobileView ? 'mobileAnimation' : 'desktopAnimation'
					}`}
					// eslint-disable-next-line @typescript-eslint/ban-ts-comment
					// @ts-ignore
					ref={headerContainer}
				>
					<Overlay isVisible={isOverlayVisible} />
					<div className="blk-ac-container-fluid container-for-user-type">
						<div className="blk-ac-row">
							<div className="blk-ac-col">
								<HeaderUserTypeDesktop
									isUserTypeOpened={isUserTypeOpened}
									isUserTypeVisible={isUserTypeVisible}
									userType={coreData.userType}
									userTypeClose={() => setIsUserTypeVisible(false)}
									userTypeHide={() => setIsUserTypeVisible(false)}
								></HeaderUserTypeDesktop>
							</div>
						</div>
					</div>
					<div className="blk-ac-container-fluid container-for-header">
						<div className="white-background"></div>
						<div className="blk-ac-row">
							<div className="blk-ac-col">
								{/* // eslint-disable-next-line @typescript-eslint/ban-ts-comment
								// @ts-ignore */}
								<div className="header-ref-element" ref={headerElement}>
									<div className="far-left">
										<HeaderHamburgerButton
											isOpened={isOpened}
											isUserTypeVisible={isUserTypeVisible}
											onClick={() => setIsOpened(!isOpened)}
										></HeaderHamburgerButton>
									</div>

									<div className="left">
										<HeaderLogo logo={coreData.logo}></HeaderLogo>
									</div>

									<div className="center">
										<HeaderNavigation
											isExpanded={isExpanded}
											isOpened={isOpened}
										>
											<HeaderUserTypeMobile
												isExpanded={isExpanded}
												isOpen={isOpened}
												isUserTypeVisible={isUserTypeVisible}
												menu={coreData.menu}
												// eslint-disable-next-line @typescript-eslint/ban-ts-comment
												// @ts-ignore
												selectedMenuIndex={coreData.selectedMenuIndex}
												userType={coreData.userType}
												selectMenuIndex={(state) =>
													setCoreData({ ...coreData, selectedMenuIndex: state })
												}
												setUserTypeVisible={(state) =>
													setIsUserTypeVisible(state)
												}
												setExpanded={(state) => setIsExpanded(state)}
												setPrimaryNavItemIndex={setPrimaryNavItemIndex}
											></HeaderUserTypeMobile>
											<HeaderPrimaryNavigation
												isExpanded={isExpanded}
												isMobile={isMobileView}
												isOpen={isOpened}
												menu={menu}
												// eslint-disable-next-line @typescript-eslint/ban-ts-comment
												// @ts-ignore
												selectedMenuIndex={coreData.selectedMenuIndex}
												setExpanded={batchedSetExpanded}
												// eslint-disable-next-line @typescript-eslint/ban-ts-comment
												// @ts-ignore
												header={headerContainer}
												primaryNavItemIndex={primaryNavItemIndex}
												setPrimaryNavItemIndex={setPrimaryNavItemIndex}
												setSecondaryNavItemIndex={setSecondaryNavItemIndex}
												onMenuItemClick={(menuItem) => {
													if (menuItem.isNotificationsMenu) {
														setIsNotificationDrawerOpen(true);
													}
												}}
											></HeaderPrimaryNavigation>
											<HeaderSecondaryNavigation
												isExpanded={isExpanded}
												isMobile={isMobileView}
												menu={menu}
												setExpanded={(state) => {
													if (!state && isMobileView) {
														resetStateOnMobile();
														return;
													}
													batchedSetExpanded(state);
												}}
												// eslint-disable-next-line @typescript-eslint/ban-ts-comment
												// @ts-ignore
												header={headerContainer}
												primaryNavItemIndex={primaryNavItemIndex}
												setPrimaryNavItemIndex={setPrimaryNavItemIndex}
												secondaryNavItemIndex={secondaryNavItemIndex}
												setSecondaryNavItemIndex={setSecondaryNavItemIndex}
											></HeaderSecondaryNavigation>
										</HeaderNavigation>
									</div>

									<div
										className={`right ${
											showNotificationAndHideProfileMenu
												? 'align-self-center'
												: ''
										}`}
									>
										{!!headerData.chatBlk && hydrated && (
											<ChatBLKIcon
												dataLinkEvent={headerData.chatBlk.dataLinkEvent}
												label={headerData.chatBlk.label}
												dataLinkEventFab={headerData.chatBlk.dataLinkEventFab}
												labelFab={headerData.chatBlk.labelFab}
												labelCoachmarkTitle={
													headerData.chatBlk.labelCoachmarkTitle
												}
												labelCoachmarkBody={
													headerData.chatBlk.labelCoachmarkBody
												}
												dataLinkEventCoachmarkDismiss={
													headerData.chatBlk.dataLinkEventCoachmarkDismiss
												}
												labelCoachmarkDismiss={
													headerData.chatBlk.labelCoachmarkDismiss
												}
											/>
										)}
										<HeaderSearch
											search={coreData.search}
											isSearchOpened={isSearchOpened}
											toggleSearch={(state) => toggleSearch(state)}
											rightAligned={!isLoggedIn}
										/>
										{showNotificationAndHideProfileMenu ? (
											<HeaderNotificationMenuItem
												apiResult={useNotificationApiResult}
												notificationsData={
													headerData.notificationsData as NotificationsData // ternary ensures this is not undefined
												}
												signInMenuItem={headerData.profile?.signIn}
												isDrawerOpen={isNotificationDrawerOpen}
												setIsDrawerOpen={setIsNotificationDrawerOpen}
												isLoggedIn={isLoggedIn}
												isMobileView={isMobileView}
											/>
										) : (
											<HeaderMyProfile
												isProfileOpened={isProfileOpened}
												profile={coreData.profile}
												setProfileOpened={(state) => setIsProfileOpened(state)}
											/>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
					{coreData.alert && (
						<AlertBar
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							url={coreData.alert.href}
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							desktopLabel={coreData.alert.displayValue}
							mobileLabel={coreData.alert.shortLabel ?? undefined}
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							target={coreData.alert.target}
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							dataLinkEvent={coreData.alert.dataLinkEvent}
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							ariaLabel={coreData.alert.ariaLabel}
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							rel={coreData.alert.rel}
							// eslint-disable-next-line @typescript-eslint/ban-ts-comment
							// @ts-ignore
							title={coreData.alert.title}
						></AlertBar>
					)}
				</div>
				{isCwp && hydrated && <MainNavToaster />}
			</>
		)
	);
};

export default Header;
